import ComponentName from '../constant/ComponentName';
import { Socket } from 'socket.io-client';
import { InstanceContainer } from '../config/binding/InstanceBinding';
import Injectables from '../constant/Injectables';
import HttpClient from '../gateway/HttpClient';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import HttpMethod from '../constant/HttpMethod';
import { ComponentWorker } from '../redux/actions/ComponentActions';
import { MemoryDb } from 'minimongo';
import { SearchResult } from '../components/custom/header/GlobalSearchComponent';
import { ImportErrorResponse } from '../components/ComponentImportComponent';
import { CancelToken } from 'axios';
import { query } from '@trlogic/client-js';
import { ChannelUndeployDependent } from '../domain/stream/transaction/ChannelUndeployDependent';
import { JobMethods } from '../constant/MethodNames';
import Filter = query.Filter;

class ComponentService {
  private readonly socket: Socket = InstanceContainer.get(Injectables.SOCKET_CLIENT);
  private readonly httpClient: HttpClient = GatewayContainer.getInstance().get(GatewayNames.API_CLIENT);

  constructor() {}

  public enter(component: ComponentName, id: string) {
    this.socket.emit('component-enter', { component, id });
  }

  public pageChange(url: string) {
    this.socket.emit('user:view-change', { url });
  }

  public mouseMove(e: MouseEvent) {
    const payload = {
      x: e.pageX,
      y: e.pageY,
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    };
    this.socket.emit('user:mouse-move', payload);
  }

  public leave(component: ComponentName, id: string) {
    this.socket.emit('component-leave', { component, id });
    this.socket.off(`component-enter-${component}`);
    this.socket.off(`component-leave-${component}`);
    this.socket.off(`component-lock-${component}`);
    this.socket.off(`component-throw-user-${component}`);
  }

  public throwUser(component: ComponentName, id: string, worker: ComponentWorker) {
    this.socket.emit(`component-throw-user`, { component, id, worker });
  }

  public listenEnter(component: ComponentName, id: string, callback: (workers: ComponentWorker[], componentId: string) => void) {
    this.socket.on(`component-enter-${component}`, (payload: { workers: ComponentWorker[]; componentId: string; component: ComponentName }) => {
      if (payload.componentId != id) return;
      callback(payload.workers, payload.componentId);
    });
  }

  public listenLeave(component: ComponentName, id: string, callback: (workers: ComponentWorker[], componentId: string) => void) {
    this.socket.on(`component-leave-${component}`, (payload: { workers: ComponentWorker[]; componentId: string; component: ComponentName }) => {
      if (payload.componentId != id) return;
      callback(payload.workers, payload.componentId);
    });
  }

  public listenLock(component: ComponentName, id: string | undefined, callback: (worker: ComponentWorker, componentId: string) => void) {
    this.socket.on(`component-lock-${component}`, (payload: { worker: ComponentWorker; componentId: string }) => {
      if (payload.componentId != id) return;
      callback(payload.worker, payload.componentId);
    });
  }

  public listenAllLock(component: ComponentName, callback: (worker: ComponentWorker, componentId: string) => void) {
    this.httpClient.execute(HttpMethod.GET, `/component/lock/${component}`).then((data: any) => {
      data.forEach((payload: { worker: ComponentWorker; componentId: string }) => {
        callback(payload.worker, payload.componentId);
      });
    });
    this.socket.on(`component-lock-${component}`, (payload: { worker: ComponentWorker; componentId: string }) => {
      callback(payload.worker, payload.componentId);
    });
  }

  public listenThrow(component: ComponentName, id: string, callback: (makerUsername: string) => void) {
    this.socket.on(`component-throw-user-${component}`, (payload: { makerUsername: string }) => {
      callback(payload.makerUsername);
    });
  }

  public listUndeployDependents(component: ComponentName, ids: string[]): Promise<ChannelUndeployDependent[]> {
    return this.httpClient.execute(HttpMethod.POST, JobMethods.UNDEPLOY_DEPENDENTS, { component, ids });
  }

  public export(component: ComponentName, id: string[], currentCategory?: string): Promise<any> {
    const payload = {
      component,
      id,
      currentCategory,
    };
    return this.httpClient.execute(HttpMethod.POST, '/component/export', payload);
  }

  public import(component: ComponentName, file: File, currentCategoryId?: string): Promise<ImportErrorResponse> {
    const payload = new FormData();
    payload.append('file', file);
    payload.append('component', component);
    payload.append('category', currentCategoryId || '');
    return this.httpClient.execute(HttpMethod.POST, '/component/import', payload);
  }

  public globalSearch(text: string): Promise<SearchResult> {
    const payload = {
      text,
    };
    return this.httpClient.execute(HttpMethod.POST, '/common/global-search', payload);
  }

  public count(
    component: ComponentName,
    componentId: string | undefined,
    token?: CancelToken,
    filter?: Filter,
  ): Promise<{
    total?: number;
    active?: number;
    draft?: number;
    failed?: number;
    inReview?: number;
    waitingForReview?: number;
  }> {
    return this.httpClient.execute(HttpMethod.GET, '/component/count', {}, { component, componentId, filter }, undefined, undefined, token);
  }
}

export interface UserSocketSession {
  id: string;
  user: { username: string; tenant: string };
  url: string;
  ip: string;
  timestamp: number;
  view?: UserSocketSessionView;
}

export interface UserSocketSessionView {
  x: number;
  y: number;
  width: number;
  height: number;
  scrollX: number;
  scrollY: number;
}

export default ComponentService;
